<template>
	<div>
		<slot/>

		<app-content>
			<page-content xs-full sm-full background>

				<lazy-cdn-background-image v-if="article.background_image && $device.isDesktop" class="fixed w-screen h-screen top-0 left-0 right-0 bottom-0"
										   :src="article.background_image"
										   :width="1920"
										   :ratio="article.background_image.ratio" cover immediate/>

				<container tight xs-full sm-full>
					<card xs-tile sm-tile :style="{borderTop: `2px solid ${article.club?.color ?? $config.public.colors.primary}`}">

						<parallax only-mobile>
							<cdn-image :src="article.image" :alt="article.title"
									   :size="{width: '100vw', md: {width: 760}}"
									   :ratio="16/9"
									   cover
									   gradient="to bottom, rgba(0,0,0,.0) 80%, rgba(0,0,0,.7)"
									   immediate class="article-image">
								<div v-if="article.image_credits" class="absolute left-0 bottom-0 mb-10 ml-16 sm:ml-24 md:ml-48">
									<a v-if="article.image_credits_url" class="flex items-center" :href="article.image_credits_url" target="_blank">
										<span class="text-3xs text-white mr-4">{{ $t('label.image_credits') }}: {{ article.image_credits }}</span>
										<icon :icon="icons.external_link" xx-small color="white"/>
									</a>
									<span v-else class="text-3xs text-white">{{ $t('label.image_credits') }}: {{ article.image_credits }}</span>
								</div>
							</cdn-image>
						</parallax>

						<chip-line v-if="article.status.value !== 'published'" :color="article.status.color" :title="article.status.title"/>

						<div class="relative bg-white pt-24 pb-48 mt--8 sm:mt-0 px-16 sm:px-24 md:px-48 rounded-t-lg md:rounded-t-none">

							<h1 class="text-4xl sm:text-5xl md:text-6xl article-title">{{ article.title }}</h1>
							<div class="flex items-center">
								<meta-text small>{{ $date(article.published_at).format('LL') }}</meta-text>
								<meta-text small>{{ $t('param_label.minutes', article.reading_time, {count: article.reading_time}) }} {{
										$t('label.reading_time')
									}}
								</meta-text>
							</div>

							<p class="text-subtext-color leading-loose mt-24 mb-16 article-description">{{ article.preview_text }}</p>

							<!-- Below Article Preview -->
							<template v-if="article.ads_enabled">
								<qmn-ad v-if="$device.isMobile" :config="QmnAds.MobileHPATopAreaAd" class="my-24"/>
								<qmn-ad v-else :config="QmnAds.DesktopIntextAd" class="my-24"/>
							</template>

							<user-content :text="article.content" large/>

							<div class="flex items-center justify-between mt-24">
								<div>
									<client-only>
										<span v-if="article.views > 1000" class="text-gray-400 text-sm">{{ article.views.toLocaleString() }} {{ $t('label.views') }}</span>
									</client-only>
								</div>

								<div class="flex justify-end">
									<like-form :action="`/v1/articles/${article.id}/likes`"
											   :count="likesCount"
											   :liked="authPermissions.has_liked"
											   :likes="likers"
											   :max="maxLikersCount"
											   @show="showLikersModal = true"
											   large
											   ref="likeForm"/>

									<tooltip bottom :text="$t('label.comments')">
										<btn @click="showComments" text x-large>
											<icon large force-size :icon="icons.comment"/>
											<span>{{ commentsCount }}</span>
										</btn>
									</tooltip>
								</div>
							</div>

							<template v-if="article.ads_enabled">
								<divider margin="40"/>

								<section id="video-tips" >
									<h2 class="text-xl truncate mb-24">{{ $t('label.more_sport_tips_in_video') }}</h2>
									<watchbetter-embed playlist="Spoferan" autoplay/>
								</section>

								<!-- Below Article Content -->
								<qmn-ad v-if="$device.isMobile" :config="QmnAds.MobileHPABottomAreaAd" class="my-24"/>
							</template>

							<divider margin="40"/>

							<section v-if="article.sportTypes?.length" id="sports" class="mb-32">
								<p class="mb-16 text-lg font-bold font-heading">{{ $t('label.sports') }}</p>
								<chip v-for="sportType in article.sportTypes" :key="'sport-' + sportType.id" class="m-2"
									  :to="$app('sports-sport', {params: {sport: sportType._key}, state: {sport_ts: sportType.cache_buster}})">{{ sportType.title }}
								</chip>
							</section>

							<section v-if="article.links?.length" id="linked" class="mb-32">
								<p class="mb-16 text-lg font-bold font-heading">{{ $t('label.linked_models') }}</p>
								<profile-preview v-for="model in article.links" :key="model.id" :model="model" outlined class="mb-16">
									<template #actions>
										<follow-icon-button :model="model"/>
									</template>
								</profile-preview>
							</section>

							<section id="author" class="mb-32">
								<p class="mb-16 text-lg font-bold font-heading">{{ $t('label.author') }}</p>
								<row>
									<column v-if="article.club" cols="12" lg="6">
										<profile-preview :model="article.club" outlined class="mb-16">
											<template #actions>
												<follow-icon-button :model="article.club"/>
											</template>
										</profile-preview>
									</column>
									<column v-if="article.author" cols="12" lg="6">
										<profile-preview :model="article.author" outlined class="mb-16">
											<template #actions>
												<follow-icon-button :model="article.author"/>
											</template>
										</profile-preview>
									</column>
								</row>
							</section>

							<qmn-ad v-if="article.ads_enabled && $device.isMobile" :config="QmnAds.MobileProspektAd"/>

							<section id="comments" class="mb-32">
								<p class="mb-24 text-lg font-bold font-heading">{{ $t('param_label.comments', commentsCount, {count: commentsCount}) }}</p>

								<comment-form v-if="$auth.loggedIn" :action="`/v1/articles/${article.id}/comments`" @success="handleCreatedComment" class="mb-12 ml--4"/>
								<lazy-login-to-see-more v-else flat :text="$t('action.login_to_comment')"/>

								<comment-feed :url="`/v1/articles/${article.id}/comments`"
											  v-model:comments-count="commentsCount"
											  v-model:comments="comments"
											  :cache-buster="article.cache_buster"/>
							</section>

						</div>
					</card>

					<card v-if="article.ads_enabled && !$device.isMobile" xs-tile sm-tile>
						<qmn-ad :config="QmnAds.DesktopProspektAd"/>
					</card>

					<div class="none sm:block fixed mb-24 bottom-0 z-2" style="margin-left: 740px">
						<client-only>
							<tooltip v-if="authPermissions.update" :text="$t('action.edit')" left class="mb-8">
								<btn icon large shadow rounded-full :to="article.club ? `https://${article.club._key}.spoferan.com/articles/${article._key}/edit` : `/my/profile/manage/blog/${article._key}/edit`">
									<icon :icon="icons.edit"/>
								</btn>
							</tooltip>
						</client-only>
						<tooltip :text="$t('action.share')" left class="mb-8">
							<btn icon large shadow rounded-full @click="indexStore.share({id: `article-${article._key}`})">
								<icon :icon="icons.share"/>
							</btn>
						</tooltip>
					</div>

				</container>

			</page-content>

		</app-content>

		<template v-if="article.ads_enabled">
			<template v-if="$device.isMobile">
				<qmn-ad :config="QmnAds.MobilePageTracker" class="none"/>
			</template>
			<template v-else>
				<qmn-ad :config="QmnAds.DesktopSitebarAd"/>
				<qmn-ad :config="QmnAds.DesktopPageTracker" class="none"/>
			</template>
		</template>

		<lazy-likes-modal v-model="showLikersModal" :count="likesCount" :url="`/v1/articles/${article.id}/likes`"/>

	</div>
</template>

<script setup lang="ts">
import {ModelType, scrollToSelector} from "@spoferan/spoferan-ts-core";
import type {Article} from "@spoferan/spoferan-ts-core";
import {mdiCommentMultiple} from '@mdi/js'
import {editIcon, externalLinkIcon, shareIcon} from "@spoferan/nuxt-spoferan/icons";
import {useIndexStore} from "../../store";
import {QmnAds} from "../../config/qmn";

const props = defineProps<{
	article: Article,
	permissions: { update: boolean }
}>();
const emit = defineEmits(['update:permissions']);

const indexStore = useIndexStore();

const {$app, $auth, $device, $apiFetch, $analytics} = useNuxtApp();
const {authPermissions, refreshAuthPermissions} = useModelAuthPermissions();
const {trackView} = useViewTracking();
const {
	comments,
	commentsCount,
	handleCreatedComment,
} = useCommentable(props.article.comments_count);

const likers = ref([]);
const likesCount = ref(0);
const showLikersModal = ref(false);

const maxLikersCount = $device.isDesktop ? 25 : 7;

const icons = {
	comment: mdiCommentMultiple,
	edit: editIcon,
	share: shareIcon,
	external_link: externalLinkIcon
};

watch(authPermissions, (newValue) => {
	emit('update:permissions', newValue);
});

// Refresh permissions on user change as required when user gets authenticated on clients side after SSR hydration
watch(() => $auth.user, (user) => {
	if (user) {
		refreshAuthPermissions(ModelType.Article, props.article.id);
	}
}, {
	immediate: true // Required for CSR
});

function showComments() {
	scrollToSelector('#comments');
}

function loadLikes() {
	$apiFetch(`/v1/articles/${props.article.id}/likes`, {
		guest: true,
		params: {
			limit: maxLikersCount,
			paginated: true
		}
	}).then(data => {
		likers.value = data.data;
		likesCount.value = data.meta.total;
	})
}

onMounted(() => {
	if (props.article.ads_enabled) {
		$analytics.trackQmnPageImpression();
	}

	trackView(ModelType.Article, props.article.id);
	loadLikes();
});

</script>

<style lang="scss">
// View Transition API
.article-image img {
	view-transition-name: article-image;
}

.article-title {
	view-transition-name: article-title;
}

.article-description {
	view-transition-name: article-description;
}

#ad-block-below-article-preview {
	min-height: 166px;

	@include media-breakpoint-down('xs') {
		min-height: 345px;
	}
}

</style>